import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  AuthUser,
  clearAccessToken,
  createPatientAccount,
  loginWithPassword,
  restoreSession,
  revokeAccessToken,
} from '../services/authApi';

import { clearAllPatientDrafts } from '../utils/patientDraft';

interface AuthContextValue {
  user: AuthUser | null;
  role: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  createUserForNutritionist: (email: string, password: string, name: string, userData: any) => Promise<string>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    restoreSession()
      .then((sessionUser) => { if (mounted) setUser(sessionUser); })
      .catch(() => { if (mounted) setUser(null); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const login = async (email: string, password: string) => {
    const sessionUser = await loginWithPassword(email, password);
    setUser(sessionUser);
  };

  const logout = async () => {
    try {
      await revokeAccessToken();
    } finally {
      clearAllPatientDrafts();
      clearAccessToken();
      setUser(null);
    }
  };

  const createUserForNutritionist = async (email: string, password: string, name: string, userData: any) => {
    return createPatientAccount(email, password, name, userData);
  };

  const value = useMemo<AuthContextValue>(() => ({
    user,
    role: user?.role || null,
    loading,
    login,
    logout,
    createUserForNutritionist,
  }), [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
